/* eslint-disable */
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Form, Input, Select, Button, Card, Space, Typography, Tooltip } from 'antd';
import { PlusOutlined, FilterOutlined, InfoCircleOutlined, PhoneOutlined } from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import { MessageSquare } from 'lucide-react';
import { CRMGroup, ScenarioTriggerDetails, WorkflowTriggerDetails } from '@/types/TCRM';
import { TCallTransferWorkflow, TTextingWorkflow } from '@/types/TWorkflows';
import { CRM_TRIGGER_TYPE, WORKFLOW_TYPE } from 'src/enums/ECRM';
import { User } from '@/types/TAccounts';
import { safeJSONParse } from '@/utils/helper';

const { Title, Text } = Typography;
const { Option, OptGroup } = Select;

interface CRMGroupCreatorProps {
	initialData?: CRMGroup;
	onSubmit: (groupData: CRMGroup) => void;
	isLoading: boolean;
	clientData: User;
}

export interface CRMGroupCreatorRef {
	resetForm: () => void;
}

const CRMGroupCreator = forwardRef<CRMGroupCreatorRef, CRMGroupCreatorProps>(
	({ initialData, onSubmit, isLoading, clientData }, ref) => {
		const [form] = Form.useForm();
		const [triggerType, setTriggerType] = useState<CRM_TRIGGER_TYPE>(
			initialData?.trigger_type || CRM_TRIGGER_TYPE.SCENARIO,
		);

		let textingWorkflow: Array<TTextingWorkflow> = [];
		let callTransferWorkflow: Array<TCallTransferWorkflow> = [];

		try {
			if (clientData?.workflows) {
				if (typeof clientData.workflows === 'string') {
					const parsed = safeJSONParse(clientData.workflows, []);
					const workflowArray = Array.isArray(parsed) ? parsed : [parsed];
					textingWorkflow = workflowArray.filter(
						(w: any) => w && typeof w === 'object' && w.id && w.textMessage !== undefined,
					) as Array<TTextingWorkflow>;
				} else if (Array.isArray(clientData.workflows)) {
					textingWorkflow = clientData.workflows.filter(
						(w: any) => w && typeof w === 'object' && w.id && w.textMessage !== undefined,
					) as Array<TTextingWorkflow>;
				}
			}

			if (clientData?.callWorkflows) {
				let rawCallWorkflows: any[] = [];

				try {
					if (typeof clientData.callWorkflows === 'string') {
						const parsed = safeJSONParse(clientData.callWorkflows, []);
						rawCallWorkflows = Array.isArray(parsed) ? parsed : [parsed];
					} else if (Array.isArray(clientData.callWorkflows)) {
						rawCallWorkflows = clientData.callWorkflows;
					}
				} catch (err) {
					console.error('Error parsing callWorkflows:', err);
				}

				callTransferWorkflow = rawCallWorkflows
					.filter(
						(w: any) => w && typeof w === 'object' && w.workflowForwardingNumber !== undefined,
					)
					.map(
						(w: any, index: number): TCallTransferWorkflow => ({
							id: w.id || `call-workflow-${index}`,
							description: w.description || 'Unnamed Call Workflow',
							workflowForwardingNumber: String(w.workflowForwardingNumber),
							workflowForwardingDID: w.workflowForwardingDID || undefined,
							voiceResponse: w.voiceResponse || undefined,
						}),
					);
			}
		} catch (error) {
			console.error('Unable to parse the workflows:', error);
		}

		useEffect(() => {
			if (initialData) {
				setTriggerType(initialData.trigger_type);
				form.setFieldsValue({
					groupID: initialData.group_id,
					groupName: initialData.group_name,
					triggerType: initialData.trigger_type,
					condition: initialData.trigger_details?.condition,
					workflow: initialData.trigger_details?.workflow?.workflow_id,
				});
			}
		}, [initialData, form]);

		const resetForm = () => {
			form.resetFields();
			setTriggerType(CRM_TRIGGER_TYPE.SCENARIO);
		};

		useImperativeHandle(ref, () => ({
			resetForm,
		}));

		const handleSubmit = () => {
			form
				.validateFields()
				.then(
					(values: {
						groupID: string;
						groupName: string;
						triggerType: string;
						condition: string;
						workflow: string;
					}) => {
						let triggerDetails: ScenarioTriggerDetails | WorkflowTriggerDetails;

						// Fix: Compare with enum values instead of string literals
						if (triggerType === CRM_TRIGGER_TYPE.SCENARIO) {
							triggerDetails = {
								condition: values.condition as string,
							};
						} else if (triggerType === CRM_TRIGGER_TYPE.WORKFLOW) {
							if (!values.workflow) {
								throw new Error('Please select a workflow');
							}

							let selectedWorkflowType: WORKFLOW_TYPE;
							let foundWorkflow = null;

							foundWorkflow = textingWorkflow.find((w) => w.id === values.workflow);

							if (foundWorkflow) {
								selectedWorkflowType = WORKFLOW_TYPE.TEXTING;
							} else {
								foundWorkflow = callTransferWorkflow.find((w) => w.id === values.workflow);

								if (foundWorkflow) {
									selectedWorkflowType = WORKFLOW_TYPE.CALL_TRANSFER;
								} else {
									throw new Error('Selected workflow not found');
								}
							}

							triggerDetails = {
								workflow: {
									workflow_id: values.workflow,
									workflow_type: selectedWorkflowType,
								},
							};
						} else {
							throw new Error('Invalid trigger type');
						}

						const groupData: CRMGroup = {
							group_id: initialData?.group_id || '',
							group_name: values.groupName,
							trigger_type: triggerType as CRM_TRIGGER_TYPE,
							trigger_details: triggerDetails,
						};

						onSubmit(groupData);
					},
				)
				.catch((errorInfo) => {
					console.log('Validation Failed:', errorInfo);
				});
		};

		const hasTextingWorkflows = textingWorkflow && textingWorkflow.length > 0;
		const hasCallWorkflows = callTransferWorkflow && callTransferWorkflow.length > 0;
		const hasAnyWorkflows = hasTextingWorkflows || hasCallWorkflows;

		return (
			<Card
				style={{
					maxWidth: 800,
					margin: '0 auto',
					boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
					borderRadius: 12,
				}}
			>
				<Title
					level={3}
					style={{
						textAlign: 'center',
						marginBottom: 24,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						gap: 12,
						color: '#333',
					}}
				>
					<FilterOutlined style={{ color: '#1890ff' }} />
					{initialData ? 'Edit Lead Group' : 'Create New Lead Group'}
				</Title>

				<Form
					form={form}
					layout="vertical"
					initialValues={{ triggerType: CRM_TRIGGER_TYPE.SCENARIO }}
				>
					<Form.Item
						name="groupName"
						label={
							<Space>
								<Text>Group Name</Text>
								<Tooltip title="Choose a descriptive name for your lead group">
									<InfoCircleOutlined style={{ color: '#1890ff' }} />
								</Tooltip>
							</Space>
						}
						rules={[
							{
								required: true,
								message: 'Please enter a group name',
							},
						]}
					>
						<Input
							placeholder="Enter group name (e.g., High-Value Leads)"
							prefix={<FilterOutlined />}
							style={{ borderRadius: 8 }}
						/>
					</Form.Item>

					<Form.Item
						label={
							<Space>
								<Text>Trigger Type</Text>
								<Tooltip title="Choose how leads will be automatically grouped">
									<InfoCircleOutlined style={{ color: '#1890ff' }} />
								</Tooltip>
							</Space>
						}
					>
						<Select
							value={triggerType}
							onChange={(value) => {
								setTriggerType(value);
								if (value === CRM_TRIGGER_TYPE.SCENARIO) {
									form.setFieldsValue({ workflow: undefined });
								}
							}}
							style={{ width: '100%', borderRadius: 8 }}
						>
							<Option value={CRM_TRIGGER_TYPE.SCENARIO}>
								<Space>
									<FilterOutlined /> Scenario-Based
								</Space>
							</Option>
							<Option value={CRM_TRIGGER_TYPE.WORKFLOW} disabled={!hasAnyWorkflows}>
								<Space>
									<PlusOutlined /> Workflow-Triggered
									{!hasAnyWorkflows && <Text type="secondary">(No workflows available)</Text>}
								</Space>
							</Option>
						</Select>
					</Form.Item>

					{triggerType === CRM_TRIGGER_TYPE.SCENARIO && (
						<Card
							type="inner"
							title="Scenario Condition"
							style={{ borderRadius: 8, marginBottom: 16 }}
						>
							<Form.Item
								name="condition"
								label={
									<Space>
										<Text>Condition</Text>
										<Tooltip title="Choose a condition to trigger this group">
											<InfoCircleOutlined style={{ color: '#1890ff' }} />
										</Tooltip>
									</Space>
								}
								rules={[{ required: true, message: 'Condition value cannot be empty' }]}
							>
								<TextArea
									placeholder="Enter condition value"
									autoSize={{ minRows: 3 }}
									style={{ width: '100%', borderRadius: 8 }}
								/>
							</Form.Item>
						</Card>
					)}

					{triggerType === CRM_TRIGGER_TYPE.WORKFLOW && (
						<Card
							type="inner"
							title="Workflow Selection"
							style={{ borderRadius: 8, marginBottom: 16 }}
						>
							{!hasAnyWorkflows ? (
								<div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
									<InfoCircleOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
									<div>No workflows available. Please create workflows first.</div>
								</div>
							) : (
								<Form.Item
									name="workflow"
									label={
										<Space>
											<Text>Select Workflow</Text>
											<Tooltip title="Choose workflow that will trigger this group">
												<InfoCircleOutlined className="text-blue-500" />
											</Tooltip>
										</Space>
									}
									rules={[{ required: true, message: 'Please select a workflow' }]}
								>
									<Select
										placeholder="Select workflow that will trigger this group"
										className="w-full"
										optionLabelProp="label"
										dropdownStyle={{ padding: 0 }}
										showSearch
										filterOption={(input, option) => {
											if (!option?.children) {
												return false;
											}
											const children = Array.isArray(option.children)
												? option.children.join(' ')
												: String(option.children);
											return children.toLowerCase().includes(input.toLowerCase());
										}}
										optionRender={(option) => {
											if (!option?.value) {
												return null;
											}

											const value = option.value as string;
											const workflow = [...textingWorkflow, ...callTransferWorkflow].find(
												(w) => w.id === value,
											);

											if (!workflow) {
												return null;
											}

											const isTextWorkflow = 'textMessage' in workflow;

											return (
												<div className="p-3 hover:bg-gray-50">
													<div className="flex items-start gap-3">
														<div
															className={`p-2 rounded-lg ${isTextWorkflow ? 'bg-green-50' : 'bg-blue-50'}`}
														>
															{isTextWorkflow ? (
																<MessageSquare className="w-5 h-5 text-green-600" />
															) : (
																<PhoneOutlined className="w-5 h-5 text-blue-600" />
															)}
														</div>
														<div className="flex-1">
															<div className="font-semibold text-gray-800">
																{workflow.description || 'Unnamed Workflow'}
															</div>
															<div className="mt-2 space-y-1">
																{isTextWorkflow ? (
																	<>
																		<div className="text-sm text-gray-600 flex items-start gap-2">
																			<span className="font-medium min-w-24">Message:</span>
																			<span className="text-gray-500">
																				{(workflow as TTextingWorkflow).textMessage?.substring(
																					0,
																					50,
																				) || 'No message'}
																				{((workflow as TTextingWorkflow).textMessage?.length || 0) >
																				50
																					? '...'
																					: ''}
																			</span>
																		</div>
																		<div className="text-sm text-gray-600 flex items-start gap-2">
																			<span className="font-medium min-w-24">Voice Response:</span>
																			<span className="text-gray-500">
																				{(workflow as TTextingWorkflow).voiceResponse?.substring(
																					0,
																					50,
																				) || 'No voice response'}
																				{((workflow as TTextingWorkflow).voiceResponse?.length ||
																					0) > 50
																					? '...'
																					: ''}
																			</span>
																		</div>
																		{(workflow as TTextingWorkflow).customTextFooter && (
																			<div className="text-sm text-gray-600 flex items-start gap-2">
																				<span className="font-medium min-w-24">Footer:</span>
																				<span className="text-gray-500">
																					{(workflow as TTextingWorkflow).customTextFooter}
																				</span>
																			</div>
																		)}
																	</>
																) : (
																	<>
																		{(workflow as TCallTransferWorkflow)
																			.workflowForwardingNumber && (
																			<div className="text-sm text-gray-600 flex items-start gap-2">
																				<span className="font-medium min-w-24">Forward to:</span>
																				<span className="text-gray-500">
																					{
																						(workflow as TCallTransferWorkflow)
																							.workflowForwardingNumber
																					}
																				</span>
																			</div>
																		)}
																		{(workflow as TCallTransferWorkflow).workflowForwardingDID && (
																			<div className="text-sm text-gray-600 flex items-start gap-2">
																				<span className="font-medium min-w-24">DID:</span>
																				<span className="text-gray-500">
																					{
																						(workflow as TCallTransferWorkflow)
																							.workflowForwardingDID
																					}
																				</span>
																			</div>
																		)}
																		{(workflow as TCallTransferWorkflow).voiceResponse && (
																			<div className="text-sm text-gray-600 flex items-start gap-2">
																				<span className="font-medium min-w-24">
																					Voice Response:
																				</span>
																				<span className="text-gray-500">
																					{(
																						workflow as TCallTransferWorkflow
																					).voiceResponse?.substring(0, 50) || 'No voice response'}
																					{((workflow as TCallTransferWorkflow).voiceResponse
																						?.length || 0) > 50
																						? '...'
																						: ''}
																				</span>
																			</div>
																		)}
																	</>
																)}
															</div>
															<div className="mt-2 pt-2 border-t border-gray-100">
																<code className="text-xs text-gray-400 font-mono">
																	ID: {workflow.id}
																</code>
															</div>
														</div>
													</div>
												</div>
											);
										}}
									>
										{hasTextingWorkflows && (
											<OptGroup
												key="texting-workflows"
												label={
													<div className="flex items-center gap-2 py-3 px-3 bg-gray-50 border-b border-gray-200">
														<MessageSquare className="w-5 h-5 text-green-600" />
														<span className="font-semibold">
															Text Message Workflows ({textingWorkflow.length})
														</span>
													</div>
												}
											>
												{textingWorkflow.map((txtWorkflow) => (
													<Option key={`texting-${txtWorkflow.id}`} value={txtWorkflow.id}>
														{txtWorkflow.description || 'Unnamed Workflow'}
													</Option>
												))}
											</OptGroup>
										)}

										{hasCallWorkflows && (
											<OptGroup
												key="call-workflows"
												label={
													<div className="flex items-center gap-2 py-3 px-3 bg-gray-50 border-b border-gray-200">
														<PhoneOutlined className="w-5 h-5 text-blue-600" />
														<span className="font-semibold">
															Call Transfer Workflows ({callTransferWorkflow.length})
														</span>
													</div>
												}
											>
												{callTransferWorkflow.map((callWorkflow) => (
													<Option key={`call-${callWorkflow.id}`} value={callWorkflow.id}>
														{callWorkflow.description || 'Unnamed Workflow'}
													</Option>
												))}
											</OptGroup>
										)}
									</Select>
								</Form.Item>
							)}
						</Card>
					)}

					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							marginTop: 24,
						}}
					>
						<Button
							type="primary"
							onClick={handleSubmit}
							icon={<PlusOutlined />}
							size="large"
							style={{
								borderRadius: 8,
								padding: '0 24px',
							}}
							loading={isLoading}
							disabled={triggerType === CRM_TRIGGER_TYPE.WORKFLOW && !hasAnyWorkflows}
						>
							{initialData ? 'Update Group' : 'Create Group'}
						</Button>
					</div>
				</Form>
			</Card>
		);
	},
);

export default CRMGroupCreator;
